import { useEffect, useReducer, useMemo, useContext, useState } from 'react'
import shopify from 'shopify-buy'
import useLocalStorage from 'react-use/lib/useLocalStorage'
import { config } from '../config'

import { currencyDef } from '../utils/formatPrice'
import { Base64 } from 'js-base64'
import { sendEvent } from './useMetrics'
import { CountryContext } from '../gatsby/wrap-wroot-element'
import { useLocation } from 'react-use'

const persistedStateId = 'shopifyCheckout'

const client = shopify.buildClient({
  domain: `${config.GATSBY_SHOPIFY_SHOP_NAME}`,
  storefrontAccessToken: process.env.GATSBY_SHOPIFY_ACCESS_TOKEN,
})

const shopifyActions = {
  setLoading: 'set_loading',
  setCheckout: 'set_checkout',
}

export const getActualShopifyId = (shopifyId, key) => {
  const decodded = Base64.decode(shopifyId)

  return decodded.replace(`gid://shopify/${key}/`, '')
}

export const getEncodedShopifyId = (id, key) => {
  return Base64.encode(`gid://shopify/${key}/${id}`)
}

const trackAddtoCart = product => {
  const dataLayerOpts = {
    event: 'dl_add_to_cart',
    ecommerce: {
      currencyCode: currencyDef,
      add: {
        products: [
          {
            name: product.title + ' ' + product.variant.title,
            id: product.variant.sku,
            product_id: getActualShopifyId(product.id, 'Product'),
            variant_id: getActualShopifyId(
              product.variant.id,
              'ProductVariant'
            ),
            price: parseFloat(product.variant.price),
            variant: product.variant.title,
            category: product.productType,
            quantity: '1',
          },
        ],
      },
    },
  }

  sendEvent(dataLayerOpts)
}

export function getTryNowAttr(customAttributes) {
  const attr = customAttributes.find(i => i.key === 'TryNow')
  return attr && attr.value
}

export function getConvertedPrices(customAttributes) {
  const convertedPrice = customAttributes.find(i => i.key === '_convertedPrice')
  const convertedCompareAtPrice = customAttributes.find(
    i => i.key === '_convertedCompareAtPrice'
  )

  return {
    convertedPrice:
      (convertedPrice && convertedPrice.attrs && convertedPrice.attrs.value) ||
      null,
    convertedCompareAtPrice:
      (convertedCompareAtPrice &&
        convertedCompareAtPrice.attrs &&
        convertedCompareAtPrice.attrs.value) ||
      null,
  }
}

export function checkForTryNowItems(items) {
  const tnItems = items.filter(i => {
    return getTryNowAttr(i.customAttributes)
  })

  return tnItems.length > 0
}

function shopifyCheckoutReducer(_, action) {
  switch (action.type) {
    case shopifyActions.setLoading:
      return { loaded: false }
    case shopifyActions.setCheckout:
      const {
        lineItems = [],
        subtotalPriceV2,
        webUrl = '',
        discountApplications = [],
        ...rest
      } = action.payload
      const isTryNowCart = checkForTryNowItems(lineItems)

      return {
        lineItems,
        subtotalPrice: subtotalPriceV2.amount || 0,
        webUrl: getWebUrl(webUrl, isTryNowCart),
        loaded: true,
        hasDiscount: discountApplications.length > 0,
        discounts: discountApplications.map(d => ({
          ...d,
          ...d.value,
          code: d.code,
        })),
      }
    default:
      throw new Error(`Action of type ${action.type} does not exist.`)
  }
}

function getWebUrl(webUrl, tryNow) {
  if (!tryNow) {
    return webUrl
  }

  // Provided by TryNow
  const url = webUrl
  const checkoutString = 'checkouts/'
  const keyString = '?key='
  const idStart = url.indexOf(checkoutString) + checkoutString.length
  const idEnd = url.indexOf(keyString)
  const keyStart = idEnd + keyString.length
  const keyEnd = url.length
  const checkoutId = url.slice(idStart, idEnd)
  const checkoutKey = url.slice(keyStart, keyEnd)
  return `https://jaxxon.trynowcheckout.com/customer-information?shopName=jaxxon-co.myshopify.com&checkout=${checkoutId}&key=${checkoutKey}`
}

const useShopifyFunctions = () => {
  const location = useLocation()
  const geoState = useContext(CountryContext)

  const [shopifyCheckoutId, setShopifyCheckoutId] = useLocalStorage(
    persistedStateId,
    ''
  )
  const [checkout, dispatch] = useReducer(shopifyCheckoutReducer, {
    loaded: false,
    subtotalPrice: 0,
    lineItems: [],
    webUrl: '',
    tryNow: false,
  })

  async function addItem({
    variantId,
    quantity,
    convertedPrice,
    convertedCompareAtPrice,
    tryNow,
    productType,
    onFinish,
  }) {
    const tryNowAttr = tryNow
      ? [
          {
            key: 'TryNow',
            value: 'true',
          },
        ]
      : []

    const customAttributes = [
      ...tryNowAttr,
      convertedPrice && {
        ...{
          key: '_convertedPrice',
          value: `${convertedPrice}`,
        },
      },
      convertedCompareAtPrice && {
        ...{
          key: '_convertedCompareAtPrice',
          value: `${convertedCompareAtPrice}`,
        },
      },
    ]

    const temporalCheckout = await client.checkout.addLineItems(
      shopifyCheckoutId,
      [{ variantId, quantity, customAttributes }]
    )
    onFinish(temporalCheckout.lineItems.length)

    const addedVariant = temporalCheckout.lineItems.find(
      i => i.variant.id === variantId
    )

    trackAddtoCart({
      ...addedVariant,
      productType,
      checkoutId: temporalCheckout.id,
    })

    dispatch({
      type: shopifyActions.setCheckout,
      payload: temporalCheckout,
    })
    return temporalCheckout
  }

  async function removeItem(variantId, onFinish) {
    const temporalCheckout = await client.checkout.removeLineItems(
      shopifyCheckoutId,
      [variantId]
    )
    onFinish(temporalCheckout.lineItems.length)
    dispatch({
      type: shopifyActions.setCheckout,
      payload: temporalCheckout,
    })

    console.log('Removed item from cart')
    console.log('onFinish function:', onFinish)
  }

  function resetCart(onFinish) {
    setShopifyCheckoutId('')
    onFinish && onFinish(0)
    dispatch({
      type: shopifyActions.setLoading,
    })
  }

  async function clearCart(onFinish) {
    const temporalCheckout = await client.checkout.removeLineItems(
      shopifyCheckoutId,
      checkout.lineItems.map(l => l.id)
    )

    onFinish && onFinish(temporalCheckout.lineItems.length)
    dispatch({
      type: shopifyActions.setCheckout,
      payload: temporalCheckout,
    })
    return temporalCheckout
  }

  async function addDiscount(discountCode, onFinish) {
    const temporalCheckout = await client.checkout
      .addDiscount(shopifyCheckoutId, discountCode)
      .then(checkout => {
        onFinish && onFinish(checkout.lineItems.length)
        dispatch({
          type: shopifyActions.setCheckout,
          payload: checkout,
        })
      })

    onFinish && onFinish(temporalCheckout.lineItems.length)
    dispatch({
      type: shopifyActions.setCheckout,
      payload: temporalCheckout,
    })
    return temporalCheckout
  }

  async function updateItem({ id, quantity }) {
    const temporalCheckout = await client.checkout.updateLineItems(
      shopifyCheckoutId,
      [
        {
          id,
          quantity,
        },
      ]
    )

    dispatch({
      type: shopifyActions.setCheckout,
      payload: temporalCheckout,
    })
    return temporalCheckout
  }

  useEffect(() => {
    if (!client || !client.checkout) return
    async function createNewCheckout() {
      const checkout = await client.checkout.create()
      setShopifyCheckoutId(checkout.id)
      return checkout
    }

    async function checkCartExistance() {
      let temporalCheckout = null
      if (shopifyCheckoutId === '') {
        temporalCheckout = await createNewCheckout()
      } else {
        temporalCheckout = await client.checkout.fetch(shopifyCheckoutId)

        const needsNewCart =
          temporalCheckout === null || temporalCheckout.completedAt

        if (needsNewCart) {
          temporalCheckout = await createNewCheckout()
        }
      }
      const params = new URLSearchParams(location.search)
      const hasDiscount = params.get('discount')
      if (hasDiscount) {
        await addDiscount(hasDiscount)
      }

      dispatch({
        type: shopifyActions.setCheckout,
        payload: temporalCheckout,
      })
    }

    if (geoState.loading === false) {
      checkCartExistance()
    }
  }, [shopifyCheckoutId, setShopifyCheckoutId, client, geoState])

  return {
    addItem,
    removeItem,
    resetCart,
    updateItem,
    clearCart,
    addDiscount,
    checkout,
  }
}

export default useShopifyFunctions
